import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import { startCase } from 'lodash';
import { reservationModesSelector } from 'slices/reservationMode.slice';
import { useAppSelector } from 'slices/store';
import {
  type DescriptionFieldSetting,
  type InSyncMode,
  type LocationTemplate,
  type OutSyncMode,
  type SyncSettingResponse,
  reservationStatuses,
} from '@timeedit/calendar-integration-api-contract';
import { TEDrawerSection } from '@timeedit/ui-components';
import {
  Collapse,
  Form,
  type FormInstance,
  Select,
  Space,
} from '@timeedit/ui-components/antd';
import LocationPicker from './components/LocationPicker';
import ReminderSelect from './components/ReminderSelect';
import ReservationValuePicker from './components/ReservationValuePicker';
import SubjectPicker from './components/SubjectPicker';

export const intlPrefix = 'integration.calendar.sync_settings';

export type EventInfoSectionProps = {
  form: FormInstance<SyncSettingResponse>;
  onChange: <Key extends keyof InSyncMode | keyof OutSyncMode>(
    name: Key,
    value: Key extends keyof OutSyncMode
      ? OutSyncMode[Key]
      : Key extends keyof InSyncMode
        ? InSyncMode[Key]
        : never
  ) => void;
  label: string;
};

type ReservationStatus = (typeof reservationStatuses)[number];

const EventInfoSection = ({ form, onChange, label }: EventInfoSectionProps) => {
  const subjectTemplates = Form.useWatch<OutSyncMode['subjectTemplates']>(
    ['syncMode', 'subjectTemplates'],
    form
  );
  const locationTemplates = Form.useWatch<OutSyncMode['locationTemplates']>(
    ['syncMode', 'locationTemplates'],
    form
  );
  const descriptionFields = Form.useWatch(
    ['syncMode', 'descriptionFields'],
    form
  );
  const reservationModeIds = Form.useWatch(
    ['syncMode', 'reservationModeIds'],
    form
  );
  const reservationStatuses = Form.useWatch(
    ['syncMode', 'reservationStatuses'],
    form
  );
  const syncType = Form.useWatch(['syncType'], form);

  const reservationModes = useAppSelector(reservationModesSelector);
  const reservationModeOptions = useMemo(
    () =>
      reservationModes.map((rm) => ({
        label: rm.name,
        value: rm.id,
      })),
    [reservationModes]
  );

  return (
    <TEDrawerSection label={label}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Form.Item
          label={getText(`${intlPrefix}.form.reservation_status.label`)}
          tooltip={getText(`${intlPrefix}.form.reservation_status.description`)}
        >
          <Select
            mode="multiple"
            placeholder={getText(
              `${intlPrefix}.form.reservation_status.placeholder`
            )}
            options={reservationStatusOptions}
            size="small"
            value={reservationStatuses}
            onChange={(values) => {
              onChange('reservationStatuses', values as ReservationStatus[]);
            }}
          />
        </Form.Item>
        <Form.Item
          label={getText(`${intlPrefix}.form.reservation_mode.label`)}
          tooltip={getText(`${intlPrefix}.form.reservation_mode.description`)}
        >
          <Select
            mode="multiple"
            placeholder={getText(
              `${intlPrefix}.form.reservation_mode.placeholder`
            )}
            options={reservationModeOptions}
            size="small"
            value={reservationModeIds}
            onChange={(values) => {
              onChange('reservationModeIds', values as number[]);
            }}
          />
        </Form.Item>
        <ReminderSelect
          form={form}
          onChange={(fields) => {
            onChange('reminder', fields);
          }}
        />
        <Collapse
          items={[
            {
              key: 'subject-fields',
              label: 'Subject fields',
              children: (
                <SubjectPicker
                  values={subjectTemplates ?? []}
                  onChange={(items) => {
                    onChange('subjectTemplates', items);
                  }}
                  addBtnLabel={getText(
                    `${intlPrefix}.form.subject_field.add_btn.label`
                  )}
                  removeBtnLabel={getText(
                    `${intlPrefix}.form.subject_field.remove_btn.label`
                  )}
                  isRoomSubject={syncType === 'ROOM'}
                />
              ),
            },
          ]}
        />
        {syncType !== 'ROOM' && (
          <Collapse
            items={[
              {
                key: 'location-fields',
                label: 'Location fields',
                children: (
                  <LocationPicker
                    values={locationTemplates ?? []}
                    onChange={(items: LocationTemplate[]) => {
                      onChange('locationTemplates', items);
                    }}
                    addBtnLabel={getText(
                      `${intlPrefix}.form.location_field.add_btn.label`
                    )}
                    removeBtnLabel={getText(
                      `${intlPrefix}.form.location_field.remove_btn.label`
                    )}
                  />
                ),
              },
            ]}
          />
        )}
        <Collapse
          items={[
            {
              key: 'description-fields',
              label: 'Description fields',
              children: (
                <ReservationValuePicker
                  values={descriptionFields ?? []}
                  onChange={(items: DescriptionFieldSetting[]) => {
                    onChange('descriptionFields', items);
                  }}
                  addBtnLabel={getText(
                    `${intlPrefix}.form.description_field.add_btn.label`
                  )}
                  removeBtnLabel={getText(
                    `${intlPrefix}.form.description_field.remove_btn.label`
                  )}
                />
              ),
            },
          ]}
        />
      </Space>
    </TEDrawerSection>
  );
};

export default EventInfoSection;

const reservationStatusOptions: Array<{
  label: string;
  value: ReservationStatus;
}> = reservationStatuses.map((status) => ({
  label: startCase(status.split('_')[1]?.toLocaleLowerCase()),
  value: status,
}));
